import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import {
  ContactNumber,
  Employee,
  PhoneNumberLabel,
} from '@employ-engage/data';
import {
  ADD_EMPLOYEE_PHONE_NUMBER,
  CONFIRM_PHONE_VERIFICATION_CODE,
  EmployEngageLogo,
  MAX_PHONE_NUMBERS,
  PHONE_NUMBER_LABELS,
  PhoneStatusBadge,
  REMOVE_EMPLOYEE_PHONE_NUMBER,
  SEND_PHONE_VERIFICATION_CODE,
  STOP_TEXTS_TO_PHONE_NUMBER,
  phoneNumberProblem,
  usePhoneLabelText,
} from '@employ-engage/common-components';

export interface PhoneConsentManagerProps {
  employee: Employee;
  companyId: string;
}

/** Where the terms and privacy policy are published: the marketing site, in every environment. */
const LEGAL_SITE = 'https://www.employengage.com';

/** A number as people write it in their own country, e.g. (801) 555-0123. */
const display = (number: string) =>
  parsePhoneNumberFromString(number)?.formatNational() ?? number;

/**
 * The consent page for a company with several numbers per person.
 *
 * Every number an employee has, with where it stands. Texts start at a number
 * only when its owner enters the code texted to it, which is the proof the
 * phone is theirs and the record that they agreed. They can also stop texts
 * at a number, remove one, or add one.
 *
 * Messages go to two live regions: `status` for what worked, `alert` for
 * what didn't, so a screen reader hears both without the focus moving.
 */
export function PhoneConsentManager({ employee, companyId }: PhoneConsentManagerProps) {
  const { t } = useTranslation(['common']);
  const labelText = usePhoneLabelText();
  const [numbers, setNumbers] = useState<ContactNumber[]>(employee.phoneNumbers ?? []);
  const [codeSentTo, setCodeSentTo] = useState<Record<string, boolean>>({});
  const [codes, setCodes] = useState<Record<string, string>>({});
  const [confirmingRemove, setConfirmingRemove] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  const [problem, setProblem] = useState('');
  const [newNumber, setNewNumber] = useState('');
  const [newLabel, setNewLabel] = useState<PhoneNumberLabel>('MOBILE');

  const [addNumber] = useMutation(ADD_EMPLOYEE_PHONE_NUMBER);
  const [sendCode] = useMutation(SEND_PHONE_VERIFICATION_CODE);
  const [confirmCode] = useMutation(CONFIRM_PHONE_VERIFICATION_CODE);
  const [stopTexts] = useMutation(STOP_TEXTS_TO_PHONE_NUMBER);
  const [removeNumber] = useMutation(REMOVE_EMPLOYEE_PHONE_NUMBER);

  const ids = { id: employee.id, companyId };

  /** Runs one change; the API's message, which is written for people, is shown on failure. */
  const run = async (key: string, work: () => Promise<string | void>) => {
    setBusy(key);
    setProblem('');
    setNotice('');
    try {
      const done = await work();
      if (done) setNotice(done);
    } catch (error: any) {
      setProblem(error?.message ?? t('Something went wrong. Please try again.'));
    } finally {
      setBusy(null);
    }
  };

  const newNumberProblem = newNumber
    ? phoneNumberProblem(newNumber, numbers.map((n) => n.number))
    : null;

  return (
    <main className="flex min-h-screen justify-center px-4 py-10">
      <div className="w-full max-w-xl">
        <div className="mb-6 flex justify-center">
          <EmployEngageLogo />
        </div>
        <h1 className="text-3xl font-bold leading-9">
          {t('hi-employee', { name: employee.firstName })}
        </h1>
        <p className="mt-2 text-gray-700">
          {t('Choose which of your phones get texts. We text a code to each number to make sure it is yours.')}
        </p>

        {/* What carriers require people to see before they opt in. */}
        <p className="mt-3 text-base text-gray-700">
          {t(
            'By confirming a number, you agree to get texts from Employ Engage about forms at your workplace. How often depends on how many forms are sent. Msg & data rates may apply. Reply STOP to stop or HELP for help.'
          )}{' '}
          <a className="font-semibold text-indigo-700 underline" href={`${LEGAL_SITE}/terms`}>
            {t('Terms and Conditions')}
          </a>
          {' · '}
          <a className="font-semibold text-indigo-700 underline" href={`${LEGAL_SITE}/privacy`}>
            {t('Privacy Policy')}
          </a>
        </p>

        <p role="status" className="mt-4 text-base text-green-800">
          {notice}
        </p>
        <p role="alert" className="mt-1 text-base text-red-700">
          {problem}
        </p>

        <h2 className="mt-6 text-lg font-semibold">{t('Your phone numbers')}</h2>
        {numbers.length === 0 && (
          <p className="mt-2 text-gray-700">{t('You have no phone numbers yet. Add one below.')}</p>
        )}
        <ul className="mt-3 space-y-4">
          {numbers.map((entry) => {
            const shown = display(entry.number);
            const codeId = `code-${entry.id}`;
            const isMobile = entry.label === 'MOBILE';
            return (
              <li key={entry.id} className="rounded-lg border border-gray-200 p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{shown}</span>
                  <span className="text-base text-gray-600">{labelText(entry.label)}</span>
                  <PhoneStatusBadge status={entry.status} />
                </div>
                {entry.addedBy !== 'EMPLOYEE' && entry.status === 'NEEDS_CONFIRMING' && (
                  <p className="mt-1 text-base text-gray-600">
                    {t('Added by your employer. Confirm it to get texts here.')}
                  </p>
                )}

                {entry.status === 'REPLIED_STOP' && (
                  <p className="mt-2 text-base text-gray-700">
                    {t('You texted STOP from this phone. To get texts here again, text START to our number from it.')}
                  </p>
                )}

                {entry.status !== 'TEXTS_ON' && entry.status !== 'REPLIED_STOP' && !isMobile && (
                  <p className="mt-2 text-base text-gray-700">
                    {t('Only mobile numbers can get texts.')}
                  </p>
                )}

                <div className="mt-3 flex flex-wrap items-end gap-2">
                  {entry.status === 'TEXTS_ON' && (
                    <button
                      type="button"
                      disabled={!!busy}
                      onClick={() =>
                        run(entry.id, async () => {
                          const { data } = await stopTexts({ variables: { ...ids, phoneNumberId: entry.id } });
                          setNumbers(data.stopTextsToPhoneNumber.phoneNumbers);
                          return t('Texts to {{number}} are off.', { number: shown });
                        })
                      }
                      className="rounded-md bg-white px-3 py-2 text-base font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 min-h-[44px] min-w-[44px]"
                    >
                      {t('Stop texts here')}
                    </button>
                  )}

                  {(entry.status === 'NEEDS_CONFIRMING' || entry.status === 'TEXTS_OFF') && isMobile && (
                    <button
                      type="button"
                      disabled={!!busy}
                      onClick={() =>
                        run(entry.id, async () => {
                          await sendCode({ variables: { ...ids, phoneNumberId: entry.id } });
                          setCodeSentTo((sent) => ({ ...sent, [entry.id]: true }));
                          return t('We texted a code to {{number}}.', { number: shown });
                        })
                      }
                      className="rounded-md bg-indigo-600 px-3 py-2 text-base font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 min-h-[44px] min-w-[44px]"
                    >
                      {codeSentTo[entry.id] ? t('Send a new code') : t('Text me a code')}
                    </button>
                  )}

                  {codeSentTo[entry.id] && entry.status !== 'TEXTS_ON' && (
                    <form
                      className="flex items-end gap-2"
                      onSubmit={(e) => {
                        e.preventDefault();
                        run(entry.id, async () => {
                          const { data } = await confirmCode({
                            variables: { ...ids, phoneNumberId: entry.id, code: codes[entry.id] ?? '' },
                          });
                          setNumbers(data.confirmPhoneVerificationCode.phoneNumbers);
                          setCodeSentTo((sent) => ({ ...sent, [entry.id]: false }));
                          return t('Texts to {{number}} are on.', { number: shown });
                        });
                      }}
                    >
                      <div>
                        <label htmlFor={codeId} className="block text-base font-medium text-gray-700">
                          {t('Code from the text')}
                        </label>
                        <input
                          id={codeId}
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          pattern="[0-9]*"
                          maxLength={6}
                          value={codes[entry.id] ?? ''}
                          onChange={(e) =>
                            setCodes((all) => ({ ...all, [entry.id]: e.target.value.replace(/\D/g, '') }))
                          }
                          className="mt-1 block w-32 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-base min-h-[44px] min-w-[44px]"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={!!busy || (codes[entry.id] ?? '').length !== 6}
                        className="rounded-md bg-indigo-600 px-3 py-2 text-base font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 min-h-[44px] min-w-[44px]"
                      >
                        {t('Confirm')}
                      </button>
                    </form>
                  )}

                  {confirmingRemove === entry.id ? (
                    <span className="flex items-center gap-2 text-base">
                      {t('Remove {{number}}?', { number: shown })}
                      <button
                        type="button"
                        disabled={!!busy}
                        onClick={() =>
                          run(entry.id, async () => {
                            const { data } = await removeNumber({ variables: { ...ids, phoneNumberId: entry.id } });
                            setNumbers(data.removeEmployeePhoneNumber.phoneNumbers);
                            setConfirmingRemove(null);
                            return t('{{number}} is removed.', { number: shown });
                          })
                        }
                        className="rounded-md bg-red-600 px-2 py-1 font-semibold text-white hover:bg-red-500 min-h-[44px] min-w-[44px]"
                      >
                        {t('Yes, remove it')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmingRemove(null)}
                        className="rounded-md px-2 py-1 font-semibold text-gray-700 hover:bg-gray-100 min-h-[44px] min-w-[44px]"
                      >
                        {t('Keep it')}
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      disabled={!!busy}
                      onClick={() => setConfirmingRemove(entry.id)}
                      aria-label={t('Remove {{number}}', { number: shown })}
                      className="rounded-md px-3 py-2 text-base font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50 min-h-[44px] min-w-[44px]"
                    >
                      {t('Remove')}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>

        {numbers.length < MAX_PHONE_NUMBERS ? (
          <form
            className="mt-8 rounded-lg bg-gray-50 p-4"
            onSubmit={(e) => {
              e.preventDefault();
              run('add', async () => {
                const { data } = await addNumber({
                  variables: { ...ids, number: newNumber.trim(), label: newLabel },
                });
                setNumbers(data.addEmployeePhoneNumber.phoneNumbers);
                setNewNumber('');
                return t('{{number}} is added. Ask for a code to start getting texts there.', {
                  number: display(newNumber.trim()),
                });
              });
            }}
          >
            <h2 className="text-lg font-semibold">{t('Add a phone number')}</h2>
            <div className="mt-3 flex flex-wrap items-end gap-2">
              <div className="min-w-[12rem] flex-1">
                <label htmlFor="new-phone-number" className="block text-base font-medium text-gray-700">
                  {t('Phone number')}
                </label>
                <input
                  id="new-phone-number"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={newNumber}
                  onChange={(e) => setNewNumber(e.target.value)}
                  aria-invalid={!!newNumberProblem || undefined}
                  aria-describedby={newNumberProblem ? 'new-phone-number-problem' : undefined}
                  placeholder={t('+1 234 567 8900 (add the country code)')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-base min-h-[44px] min-w-[44px]"
                />
              </div>
              <div>
                <label htmlFor="new-phone-label" className="block text-base font-medium text-gray-700">
                  {t('Label')}
                </label>
                <select
                  id="new-phone-label"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value as PhoneNumberLabel)}
                  className="mt-1 block rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-base min-h-[44px] min-w-[44px]"
                >
                  {PHONE_NUMBER_LABELS.map((label) => (
                    <option key={label} value={label}>
                      {labelText(label)}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="submit"
                disabled={!!busy || !newNumber.trim() || !!newNumberProblem}
                className="rounded-md bg-indigo-600 px-3 py-2 text-base font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 min-h-[44px] min-w-[44px]"
              >
                {t('Add number')}
              </button>
            </div>
            {newNumberProblem && (
              <p id="new-phone-number-problem" className="mt-1 text-base text-red-700">
                {newNumberProblem === 'duplicate'
                  ? t('This number is already on the list.')
                  : t('Please enter a valid phone number with country code (e.g. +12345678900)')}
              </p>
            )}
          </form>
        ) : (
          <p className="mt-8 text-base text-gray-600">
            {t('You can have up to {{max}} numbers.', { max: MAX_PHONE_NUMBERS })}
          </p>
        )}

        <p className="mt-8 text-base text-gray-600">
          {t('You can stop texts at any time here, or by replying STOP to any text.')}
        </p>
      </div>
    </main>
  );
}

export default PhoneConsentManager;
